//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { IAutomateRightsRequestingState } from '@account-settings/redux/rights-management/automateRightsRequestingModel';
import * as rightsAutomationAPi from '@apis/rights-management/rightsAutomationApi';
import { IAutomationConfigurations } from '@apis/rights-management/rightsAutomationApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import PartialCuid from '@models/PartialCuid';
import { SliceNames } from '@redux/SliceNames';

const initialState: IAutomateRightsRequestingState = {
  modalStates: {
    isOpen: false,
  },
  loading: false,
  error: '',
  selectedToggleValue: null,
  selectedCompaigns: [],
  automationConfigurations: {
    isAutomated: null,
    rightsOptionsIds: [],
  },
};

export const getRightsAutomationConfigurations = createAsyncThunk(
  `${SliceNames.AccountSettingsRightsManagement}/getConfigurations`,
  async () => {
    const configs = await rightsAutomationAPi.getAutomationConfigurations();
    return configs;
  },
);

export const updateRightsAutomationConfigurations = createAsyncThunk(
  `${SliceNames.AccountSettingsRightsManagement}/updateConfigurations`,
  async (configuration: IAutomationConfigurations, thunkAPI) => {
    const res =
      await rightsAutomationAPi.updateAutomationConfigurations(configuration);

    thunkAPI.dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Success',
        message: 'Automatic rights request settings saved successfully',
        type: ToastType.SUCCESS,
      }),
    );

    return res;
  },
);

export const sendMediasToRightAutomation = createAsyncThunk(
  `${SliceNames.AccountSettingsRightsManagement}/sendMediaToAutoRights`,
  async (cuids: PartialCuid[], thunkAPI) => {
    try {
      const res =
        await rightsAutomationAPi.sendContentsToRightAutomation(cuids);

      thunkAPI.dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Success',
          message: 'Right request scheduled successfully',
          type: ToastType.SUCCESS,
        }),
      );

      return res;
    } catch (_e) {
      thunkAPI.dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Error',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
        }),
      );
    }
  },
);

const { actions, reducer } = createSlice({
  name: SliceNames.AccountSettingsRightsManagement,
  initialState,
  reducers: {
    openAutomateRightsRequuestingModal(state) {
      state.modalStates.isOpen = true;
    },
    closeAutomateRightsRequestingModal(state) {
      state.modalStates.isOpen = false;
      state.selectedToggleValue = state.automationConfigurations.isAutomated;
      state.selectedCompaigns = state.automationConfigurations.rightsOptionsIds;
      state.error = '';
    },
    setBrandMentionedToggleValue(state, action: PayloadAction<boolean>) {
      state.selectedToggleValue = action.payload;
    },

    setSelectedCompaignIds(
      state,
      action: PayloadAction<{ id: number; isChecked: boolean }>,
    ) {
      if (action.payload.isChecked) {
        state.selectedCompaigns.push(action.payload.id);
      } else {
        const compaignId = action.payload.id;
        state.selectedCompaigns = state.selectedCompaigns.filter(
          (item) => item !== compaignId,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRightsAutomationConfigurations.pending, (state) => {
      state.loading = true;
    }),
      builder.addCase(
        getRightsAutomationConfigurations.fulfilled,
        (state, action: PayloadAction<IAutomationConfigurations>) => {
          state.loading = false;
          state.error = '';
          state.selectedCompaigns = action.payload.rightsOptionsIds;
          state.selectedToggleValue = action.payload.isAutomated;
          state.automationConfigurations = {
            isAutomated: action.payload.isAutomated,
            rightsOptionsIds: action.payload.rightsOptionsIds,
          };
        },
      ),
      builder.addCase(
        getRightsAutomationConfigurations.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message ?? '';
        },
      ),
      builder.addCase(updateRightsAutomationConfigurations.pending, (state) => {
        state.loading = true;
      }),
      builder.addCase(
        updateRightsAutomationConfigurations.fulfilled,
        (state) => {
          state.loading = false;
          state.error = '';
          state.automationConfigurations.rightsOptionsIds =
            state.selectedCompaigns;
          state.automationConfigurations.isAutomated =
            state.selectedToggleValue;
          state.modalStates.isOpen = false;
        },
      ),
      builder.addCase(
        updateRightsAutomationConfigurations.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message ?? '';
        },
      );
  },
});

export const {
  closeAutomateRightsRequestingModal,
  openAutomateRightsRequuestingModal,
  setBrandMentionedToggleValue,
  setSelectedCompaignIds,
} = actions;

export default reducer;
